import { createFileRoute, redirect, Link } from '@tanstack/react-router'
import { Upload, Server, Box, Database, Loader2, ChevronLeft, ChevronRight, CheckCircle2, Clock, CircleAlert, CircleSlash } from 'lucide-react'
import { useState, useRef, useCallback, useEffect, useMemo, useSyncExternalStore } from 'react'
import { useClientProject } from '@/contexts'
import { authService } from '@/services/authService'
import { getProjectAccessLevel } from '@/services/userManagementService'
import { isGlobalAdmin } from '@/types'
import {
  getRecentUploads,
  type RecentUpload,
  type ManagementAccountCategory,
} from '@/services/projectService'
import { uploadContractsTracked, uploadManagementAccountsTracked } from '@/services/uploadWithProgress'
import { CONTRACT_CATEGORIES, toCategoryValue } from '@/lib/contractCategories'
import { flattenFolderTree, folderTreePrefix, type FlatFolderNode } from '@/services/folderService'
import {
  subscribeToBatches,
  getAllBatchSnapshots,
  isTerminalStatus,
  type UploadItem,
} from '@/services/uploadBatchStore'
import { UploadInProgressPanel } from '@/components/UploadInProgressPanel'
import '@/styles/pages.css'
import { PageHeader } from '@/components/page/PageHeader'
import './-data-center/dataCenter.css'

export const Route = createFileRoute('/_app/organization/vault')({
  head: () => ({ meta: [{ title: 'Data Center · AERRIS' }] }),
  // The Data Center is project-scoped and admin-only (mirrors its nav visibility): it requires an
  // open project, and the caller must be a global admin or an admin of that project. Enforced here
  // before render so it can't be reached by direct URL. Non-qualifying callers fall back to the
  // agreements vault, which scopes them to what they can access.
  beforeLoad: async ({ search }) => {
    const session = await authService.ensureSession()
    // Not signed in / server unreachable: let the app shell (_app) and the API 401 handler decide.
    if (session.status !== 'authenticated') return
    const { projectId } = search
    if (!projectId) throw redirect({ to: '/law/vault', search: true, replace: true })
    if (isGlobalAdmin(session.user.role)) return
    // The access-level endpoint resolves inheritance, so a client admin of the project's client
    // also reads as ADMIN here. A 403 (no access at all) is caught and treated as "not admin".
    const level = await getProjectAccessLevel(projectId).catch(() => null)
    if (level !== 'ADMIN') throw redirect({ to: '/law/vault', search: true, replace: true })
  },
  component: OrganizationVaultPage,
})

// Data-room connectors are not built yet; the card lists what is planned, with no controls.
const PLANNED_DATA_ROOMS = [
  { name: 'Intralinks', kind: 'Virtual data room', Icon: Database },
  { name: 'Box', kind: 'Cloud storage', Icon: Box },
  { name: 'SFTP', kind: 'Secure file transfer', Icon: Server },
]

/** One Recent Uploads row: a live SSE-tracked file or a persisted document. */
interface RecentUploadRow {
  key: string
  /** Persisted document uuid, for the name/analysis link. Null for in-progress live rows. */
  uuid: string | null
  kind: 'CONTRACT' | 'MANAGEMENT_ACCOUNT' | null
  name: string
  category: string
  /** Folder the contract sits in, or null (project root, or a management account). */
  folderId: string | null
  uploadedAt: string | null
  status: 'queued' | 'processing' | 'loaded' | 'skipped' | 'failed'
  detail?: string | null
}

const PAGE_SIZE = 10

const STATUS_ICON: Record<RecentUploadRow['status'], typeof Clock> = {
  queued: Clock,
  processing: Loader2,
  loaded: CheckCircle2,
  skipped: CircleSlash,
  failed: CircleAlert,
}

const STATUS_LABEL: Record<RecentUploadRow['status'], string> = {
  queued: 'Queued',
  processing: 'Processing...',
  loaded: 'Loaded',
  skipped: 'Skipped',
  failed: 'Failed',
}

function liveItemStatus(item: UploadItem): RecentUploadRow['status'] {
  switch (item.status) {
    case 'QUEUED': return 'queued'
    case 'PROCESSING': return 'processing'
    case 'COMPLETED': return 'loaded'
    case 'SKIPPED': return 'skipped'
    default: return 'failed'
  }
}

function formatCategory(category: string | null): string {
  if (!category) return '—'
  if (category === 'managementAccount') return 'Management account'
  return category.charAt(0).toUpperCase() + category.slice(1)
}

// The document-type selector, split into three visual sections (native <optgroup>s): auto-detect,
// the contract Categories, and per-category management accounts. Options carry an explicit value so
// the two pipelines can be distinguished: contract options (and "Detect") use their label as the
// value — toCategoryValue collapses "Detect" to 'detect' — while each management-account option uses
// a `ma:`-prefixed value (`ma:client`, …) that managementAccountCategory() decodes to the bare
// category the server expects. The shared CONTRACT_CATEGORIES is the single source of truth for the
// middle group. NB: keep "Detect" a single word — toCategoryValue relies on it.
const MA_OPTION_PREFIX = 'ma:'

const CATEGORY_OPTION_GROUPS: { label: string; options: { value: string; label: string }[] }[] = [
  { label: 'Auto-detect', options: [{ value: 'Detect', label: 'Detect' }] },
  { label: 'Contract type', options: CONTRACT_CATEGORIES.map((c) => ({ value: c, label: c })) },
  {
    label: 'Management account',
    options: [
      { value: `${MA_OPTION_PREFIX}client`, label: 'Management account · Client' },
      { value: `${MA_OPTION_PREFIX}lease`, label: 'Management account · Lease' },
      { value: `${MA_OPTION_PREFIX}employee`, label: 'Management account · Employee' },
      { value: `${MA_OPTION_PREFIX}supplier`, label: 'Management account · Supplier' },
    ],
  },
]
// "Detect" is the default: uploads auto-detect their contract type unless the user picks otherwise.
const DEFAULT_DOCUMENT_TYPE = 'Detect'

/** The management-account category (client/lease/employee/supplier) a documentType selects, or null
 *  when the selected type is a contract Category / Detect (i.e. the standard contract pipeline). */
function managementAccountCategory(documentType: string): ManagementAccountCategory | null {
  return documentType.startsWith(MA_OPTION_PREFIX)
    ? (documentType.slice(MA_OPTION_PREFIX.length) as ManagementAccountCategory)
    : null
}

function OrganizationVaultPage() {
  const { clientId, projectId, selectedClient, selectedProject } = useClientProject()
  const [documentType, setDocumentType] = useState<string>(DEFAULT_DOCUMENT_TYPE)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadStatus, setUploadStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  // Folder destination for contract uploads ('' = project root). Folders don't apply to
  // management accounts, so the selector is hidden for that document type.
  const [folders, setFolders] = useState<FlatFolderNode[]>([])
  const [selectedFolderId, setSelectedFolderId] = useState<string>('')
  // Management-account reporting period + optional label. Revealed only for management-account
  // document types; the period is required (end on/after start) before the upload is allowed.
  const [periodStart, setPeriodStart] = useState<string>('')
  const [periodEnd, setPeriodEnd] = useState<string>('')
  const [maLabel, setMaLabel] = useState<string>('')
  const maCategory = managementAccountCategory(documentType)
  const isManagementAccount = maCategory != null
  const periodValid = periodStart !== '' && periodEnd !== '' && periodEnd >= periodStart

  // Load the project's folder tree (flattened, with tree-line guides) for the destination selector.
  useEffect(() => {
    if (!projectId) {
      setFolders([])
      setSelectedFolderId('')
      return
    }
    let cancelled = false
    const load = async () => {
      let flat: FlatFolderNode[] = []
      try { flat = await flattenFolderTree(projectId) } catch { /* leave folders empty on failure */ }
      if (!cancelled) {
        setFolders(flat)
        setSelectedFolderId('')
      }
    }
    load()
    return () => { cancelled = true }
  }, [projectId])

  // ---- Recent uploads: server-paged persisted rows + live overlay from the SSE batch store ----
  // Server-side paging (10/page). Live in-progress rows are pinned to the top of page 1 only; deeper
  // pages show server rows alone. Page resets to the first page whenever the project changes.
  const [persistedUploads, setPersistedUploads] = useState<RecentUpload[]>([])
  const [uploadsTotal, setUploadsTotal] = useState(0)
  const [uploadsPage, setUploadsPage] = useState(0)
  const liveBatches = useSyncExternalStore(subscribeToBatches, getAllBatchSnapshots)

  useEffect(() => { setUploadsPage(0) }, [projectId])

  // Fast lookup of a folder's display path from the already-loaded flattened tree.
  const foldersByUuid = useMemo(() => {
    const m = new Map<string, FlatFolderNode>()
    folders.forEach((n) => m.set(n.folder.uuid, n))
    return m
  }, [folders])

  // Build a "Parent / Child" label for a folder by walking parentId up the loaded tree.
  const folderLabel = useCallback((folderId: string): string => {
    const names: string[] = []
    const seen = new Set<string>()
    let cursor: string | null = folderId
    while (cursor && !seen.has(cursor)) {
      seen.add(cursor)
      const node = foldersByUuid.get(cursor)
      if (!node) break
      names.unshift(node.folder.name)
      cursor = node.folder.parentId ?? null
    }
    return names.length ? names.join(' / ') : 'Folder'
  }, [foldersByUuid])

  // Refetch the current page when the project or page changes, and whenever a batch settles (so
  // freshly processed documents move from the live overlay into the persisted rows on page 1).
  const completedBatchCount = liveBatches.filter((b) => b.completed).length
  useEffect(() => {
    if (!projectId) {
      setPersistedUploads([])
      setUploadsTotal(0)
      return
    }
    let cancelled = false
    getRecentUploads(projectId, uploadsPage * PAGE_SIZE, PAGE_SIZE)
      .then((page) => {
        if (cancelled) return
        setPersistedUploads(page.rows)
        setUploadsTotal(page.total)
      })
      .catch((err) => console.warn('Failed to fetch recent uploads:', err))
    return () => { cancelled = true }
  }, [projectId, uploadsPage, completedBatchCount])

  const recentUploadRows = useMemo<RecentUploadRow[]>(() => {
    const persisted: RecentUploadRow[] = persistedUploads.map((p) => ({
      key: `persisted-${p.uuid}`,
      uuid: p.uuid,
      kind: p.kind,
      name: p.name || 'Untitled',
      category: formatCategory(p.category),
      folderId: p.folderId,
      uploadedAt: p.createdAt,
      status: 'loaded' as const,
    }))

    // Live rows are client-side and unpersisted, so they only make sense on the first page.
    if (uploadsPage !== 0) return persisted

    const liveItems = liveBatches.flatMap((b) => b.items)
    // A successfully loaded live item reappears in the persisted refetch under its extracted
    // document name — drop the live row once that happens. Failed and skipped rows stay visible
    // so the outcome isn't silently lost.
    const live: RecentUploadRow[] = liveItems
      .filter((i) => !(
        isTerminalStatus(i.status)
        && i.contractUuid != null
        && persistedUploads.some((p) => p.uuid === i.contractUuid)
      ))
      .map((i) => ({
        key: `live-${i.itemId}`,
        uuid: null,
        kind: null,
        name: i.fileName || 'Unnamed file',
        category: '—',
        folderId: null,
        uploadedAt: null,
        status: liveItemStatus(i),
        detail: i.detail,
      }))
      .reverse() // newest batch items first

    return [...live, ...persisted]
  }, [liveBatches, persistedUploads, uploadsPage])

  const pageCount = Math.max(1, Math.ceil(uploadsTotal / PAGE_SIZE))

  const handleUpload = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return
    if (!projectId) {
      setUploadStatus({ type: 'error', message: 'Please select a client and project before uploading.' })
      return
    }

    const maCat = managementAccountCategory(documentType)
    // Management accounts require a valid reporting period (end on/after start) before uploading.
    if (maCat && !(periodStart !== '' && periodEnd !== '' && periodEnd >= periodStart)) {
      setUploadStatus({ type: 'error', message: 'Set a period start and end (end on or after start) before uploading a management account.' })
      return
    }

    setIsUploading(true)
    setUploadStatus(null)

    try {
      if (maCat) {
        // A management account is a single CSV; the server mints the batchId and streams its
        // one-item progress over SSE just like a contract batch.
        await uploadManagementAccountsTracked(
          projectId,
          files[0],
          maCat,
          periodStart,
          periodEnd,
          maLabel.trim() || null,
        )
        setUploadStatus({ type: 'success', message: 'Management account uploaded — processing in the background.' })
      } else {
        const category = toCategoryValue(documentType)
        // Tracked: drives the two-phase upload toast (byte upload → background processing).
        await uploadContractsTracked(projectId, Array.from(files), category, selectedFolderId || null)
        setUploadStatus({ type: 'success', message: `All ${files.length} file(s) uploaded — processing in the background.` })
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Upload failed. Please try again.'
      setUploadStatus({ type: 'error', message })
    } finally {
      setIsUploading(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }, [projectId, documentType, selectedFolderId, periodStart, periodEnd, maLabel])

  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleUpload(e.target.files)
  }

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    handleUpload(e.dataTransfer.files)
  }

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const triggerFileSelect = () => {
    fileInputRef.current?.click()
  }

  const processingCount = recentUploadRows.filter((r) => r.status === 'queued' || r.status === 'processing').length

  return (
    <div className="page-shell page-shell--scroll">
      <PageHeader
        crumbs={[
          ...(selectedClient ? [{ label: selectedClient.name }] : []),
          ...(selectedProject ? [{ label: selectedProject.name }] : []),
        ]}
        title="Data Center"
        subtitle="Bulk intake for this project. Aerris reads, tags and risk-scores every file after upload."
      />

      <div className="dc-grid">
        <section className="page-card dc-card" aria-labelledby="dc-upload-title">
          <div className="dc-card-head">
            <h2 id="dc-upload-title" className="dc-card-title">Upload files</h2>
            <span className="dc-card-sub">
              {isManagementAccount ? 'One management-account CSV at a time.' : 'Contract PDFs; choose several at once.'}
            </span>
          </div>

          <div
            className="dc-dropzone"
            onDrop={onDrop}
            onDragOver={onDragOver}
            onClick={() => { if (!isUploading && !(isManagementAccount && !periodValid)) triggerFileSelect() }}
          >
            <Upload size={24} strokeWidth={1.5} />
            <span className="dc-dropzone-title">Drop files here to upload</span>
            <span className="dc-dropzone-sub">or click to browse your computer</span>
          </div>

          <div className="dc-fields">
            <label className="dc-field">
              Document type
              <select
                className="dc-select"
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value)}
              >
                {CATEGORY_OPTION_GROUPS.map((group) => (
                  <optgroup key={group.label} label={group.label}>
                    {group.options.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </label>
            {!isManagementAccount && (
              <label className="dc-field">
                Destination folder
                <select
                  className="dc-select"
                  value={selectedFolderId}
                  onChange={(e) => setSelectedFolderId(e.target.value)}
                  title="Destination folder"
                >
                  <option value="">Project root</option>
                  {folders.map((node) => (
                    <option key={node.folder.uuid} value={node.folder.uuid}>
                      {folderTreePrefix(node) + node.folder.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {isManagementAccount && (
              <>
                <label className="dc-field">
                  Period start
                  <input
                    type="date"
                    className="dc-select"
                    value={periodStart}
                    max={periodEnd || undefined}
                    onChange={(e) => setPeriodStart(e.target.value)}
                  />
                </label>
                <label className="dc-field">
                  Period end
                  <input
                    type="date"
                    className="dc-select"
                    value={periodEnd}
                    min={periodStart || undefined}
                    onChange={(e) => setPeriodEnd(e.target.value)}
                  />
                </label>
                <label className="dc-field">
                  Label (optional)
                  <input
                    type="text"
                    className="dc-select"
                    value={maLabel}
                    placeholder="e.g. FY24"
                    onChange={(e) => setMaLabel(e.target.value)}
                  />
                </label>
              </>
            )}
          </div>

          <div className="dc-actions">
            <button
              type="button"
              className="page-btn page-btn--primary"
              onClick={triggerFileSelect}
              disabled={isUploading || (isManagementAccount && !periodValid)}
            >
              {isUploading ? <Loader2 size={16} className="loading-spinner" /> : <Upload size={16} />}
              {isUploading ? 'Uploading...' : 'Select Files'}
            </button>
          </div>

          {uploadStatus && (
            <div className={`dc-status dc-status--${uploadStatus.type}`} role="status">
              {uploadStatus.message}
            </div>
          )}
          {!projectId && (
            <div className="dc-card-sub">Select a client and project to enable uploads.</div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            multiple={!isManagementAccount}
            accept={isManagementAccount ? '.csv,text/csv' : undefined}
            style={{ display: 'none' }}
            onChange={onFileInputChange}
          />
        </section>

        <section className="page-card dc-card" aria-labelledby="dc-rooms-title">
          <div className="dc-card-head dc-card-head--split">
            <div>
              <h2 id="dc-rooms-title" className="dc-card-title">Data room connections</h2>
              <span className="dc-card-sub">Sync a virtual data room into this project automatically.</span>
            </div>
            <span className="dc-soon">Coming soon</span>
          </div>
          <ul className="dc-rooms">
            {PLANNED_DATA_ROOMS.map(({ name, kind, Icon }) => (
              <li key={name}>
                <span className="dc-room-icon"><Icon size={16} /></span>
                <span className="dc-room-text">
                  <span className="dc-room-name">{name}</span>
                  <span className="dc-card-sub">{kind}</span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <UploadInProgressPanel />

      <section className="page-card" aria-labelledby="dc-recent-title">
        <div className="dc-recent-head">
          <h2 id="dc-recent-title" className="dc-card-title">Recent uploads</h2>
          {processingCount > 0 && (
            <span className="dc-card-sub">{processingCount} still processing</span>
          )}
        </div>
        <div className="table-container dc-recent-table">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Folder</th>
                <th>Category</th>
                <th>Uploaded</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {recentUploadRows.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ color: 'var(--text-muted)' }}>
                    {projectId ? 'No uploads yet for this project.' : 'Select a client and project to see uploads.'}
                  </td>
                </tr>
              )}
              {recentUploadRows.map((row) => {
                const StatusIcon = STATUS_ICON[row.status]
                return (
                  <tr key={row.key}>
                    <td>
                      {row.uuid ? (
                        <Link
                          to="/law/vault/analysis/$contractId"
                          params={{ contractId: row.uuid }}
                          search={{
                            ...(row.kind === 'MANAGEMENT_ACCOUNT' ? { kind: 'managementAccount' as const } : {}),
                          }}
                          style={{ color: 'var(--text-primary)', textDecoration: 'none', fontWeight: 500 }}
                        >
                          {row.name}
                        </Link>
                      ) : (
                        <span style={{ fontWeight: 500 }}>{row.name}</span>
                      )}
                    </td>
                    <td>
                      {row.uuid == null || row.kind === 'MANAGEMENT_ACCOUNT' ? (
                        <span style={{ color: 'var(--text-muted)' }}>—</span>
                      ) : row.folderId ? (
                        <Link
                          to="/law/vault"
                          search={{
                            clientId: clientId ?? '',
                            projectId: projectId ?? '',
                            folderId: row.folderId,
                          }}
                          style={{ color: 'var(--accent-ink)', textDecoration: 'none' }}
                        >
                          {folderLabel(row.folderId)}
                        </Link>
                      ) : (
                        <span style={{ color: 'var(--text-secondary)' }}>Project root</span>
                      )}
                    </td>
                    <td style={{ color: 'var(--text-secondary)' }}>{row.category}</td>
                    <td style={{ color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
                      {row.uploadedAt ? new Date(row.uploadedAt).toLocaleString() : 'Just now'}
                    </td>
                    <td>
                      <span className={`dc-upload-status dc-upload-status--${row.status}`} title={row.detail ?? undefined}>
                        <StatusIcon size={14} className={row.status === 'processing' ? 'loading-spinner' : undefined} aria-hidden />
                        {STATUS_LABEL[row.status]}
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        {uploadsTotal > PAGE_SIZE && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '0.75rem',
              padding: '0.75rem 1rem',
              fontSize: '0.8125rem',
              color: 'var(--text-secondary)',
            }}
          >
            <span>Page {uploadsPage + 1} of {pageCount}</span>
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => setUploadsPage((p) => Math.max(0, p - 1))}
              disabled={uploadsPage === 0}
              aria-label="Previous page"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => setUploadsPage((p) => Math.min(pageCount - 1, p + 1))}
              disabled={uploadsPage >= pageCount - 1}
              aria-label="Next page"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        )}
      </section>
    </div>
  )
}
